<template>
  <slot />
</template>

<script>
import { provide, ref } from 'vue'
import { usersApi } from '../api/users.js'
import { sessionApi } from '../api/session.js'
import { getCsrfToken, setCsrfToken } from '../config/api.js'

export default {
  name: 'AuthProvider',
  setup() {
    const user = ref(null)
    const users = ref([])

    const fetchUsers = async () => {
      try {
        const response = await usersApi.getUsers()
        users.value = response.data || []
      } catch (err) {
        // Handle error silently - users list will be empty
      }
    }

    // Fetch users on setup
    fetchUsers()

    const determineUserRole = (roleId) => {
      // Map role_id to role names based on backend roles
      // 1 = employee, 2 = manager, 3 = admin
      switch(roleId) {
        case 3:
          return 'admin'
        case 2:
          return 'manager'
        case 1:
        default:
          return 'employee'
      }
    }

    const login = async (email, password) => {
      try {
        const response = await sessionApi.login(email, password)
        
        if (response.message === 'Login successful') {
          const foundUser = await usersApi.getUser(response.user_id)
          
          if (foundUser.data) {
            user.value = {
              id: foundUser.data.id,
              firstName: foundUser.data.first_name,
              lastName: foundUser.data.last_name,
              email: foundUser.data.email,
              role: determineUserRole(response.role_id)
            }
            return true
          }
        }
        return false
      } catch (error) {
        return false
      }
    }

    // Demo login is now handled in LoginPage with real authentication

    const logout = async () => {
      try {
        await sessionApi.logout()
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        user.value = null
        // Refetch users list after logout
        await fetchUsers()
      }
    }

    const updateUser = async (updates) => {
      if (user.value) {
        try {
          // Map frontend field names to backend field names
          const backendUpdates = {
            first_name: updates.firstName,
            last_name: updates.lastName,
            email: updates.email
          }
          
          const response = await usersApi.updateUser(user.value.id, backendUpdates)
          if (response.data) {
            // Update local user state with the response data
            user.value = {
              ...user.value,
              firstName: response.data.first_name,
              lastName: response.data.last_name,
              email: response.data.email
            }
            
            // Refresh the users list to include the updated email
            await fetchUsers()
            
            return { success: true }
          }
        } catch (err) {
          // Parse error message for better user feedback
          let errorMessage = 'Failed to update user'
          try {
            // Try to parse the error message as JSON first
            if (err.message && err.message.startsWith('{')) {
              const errorData = JSON.parse(err.message)
              if (typeof errorData === 'object' && errorData.errors) {
                errorMessage = Object.values(errorData.errors).flat().join(', ')
              } else if (typeof errorData === 'object') {
                errorMessage = Object.values(errorData).flat().join(', ')
              } else {
                errorMessage = errorData
              }
            } else {
              errorMessage = err.message
            }
          } catch {
            errorMessage = err.message || 'An unknown error occurred'
          }
          throw new Error(errorMessage)
        }
      }
    }

    const deleteAccount = async () => {
      if (user.value) {
        try {
          await usersApi.deleteUser(user.value.id)
          user.value = null
          // Refetch users list after deletion
          await fetchUsers()
          return { success: true }
        } catch (err) {
          // Parse error message for better user feedback
          let errorMessage = 'Failed to delete user'
          try {
            // Try to parse the error message as JSON first
            if (err.message && err.message.startsWith('{')) {
              const errorData = JSON.parse(err.message)
              if (typeof errorData === 'object' && errorData.errors) {
                errorMessage = Object.values(errorData.errors).flat().join(', ')
              } else if (typeof errorData === 'object') {
                errorMessage = Object.values(errorData).flat().join(', ')
              } else {
                errorMessage = errorData
              }
            } else {
              errorMessage = err.message
            }
          } catch {
            errorMessage = err.message || 'An unknown error occurred'
          }
          throw new Error(errorMessage)
        }
      }
    }

    const isAuthenticated = () => {
      return !!getCsrfToken() && !!user.value
    }

    const clearAuthState = () => {
      user.value = null
      setCsrfToken(null)
    }

    const authState = {
      user,
      users,
      login,
      logout,
      updateUser,
      deleteAccount,
      fetchUsers,
      isAuthenticated,
      clearAuthState
    }

    provide('auth', authState)

    return {}
  }
}
</script>